"use client";

import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-2">
        <div className="h-6 w-16 bg-muted/60 animate-pulse rounded-md" data-index={0} />
        <div className="h-6 w-20 bg-muted/60 animate-pulse rounded-md" data-index={1} />
        <div className="h-6 w-16 bg-muted/60 animate-pulse rounded-md" data-index={2} />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-6 lg:gap-10">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative inline-flex items-center justify-center text-foreground bg-transparent hover:bg-transparent focus:bg-transparent px-1 py-2 text-sm font-medium tracking-[0.04em]" style={{
            fontFamily: "var(--font-default)"
          }}>
                <span className="relative">
                  {item.label}
                  <svg className="absolute -bottom-1 left-0 w-full h-[6px] pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-[320ms]" viewBox="0 0 100 6" preserveAspectRatio="none" aria-hidden="true">
                    <path d="M2,3 Q15,1 30,3 T60,3 T95,3" stroke="hsl(350 70% 65%)" strokeWidth="1.5" strokeLinecap="round" fill="none" strokeDasharray="100" strokeDashoffset="100" className="group-hover:[stroke-dashoffset:0] transition-[stroke-dashoffset] duration-[320ms] ease-out" />
                  </svg>
                </span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-3 px-2">
        <div className="h-8 w-3/4 bg-muted/60 animate-pulse rounded-md" data-index={0} />
        <div className="h-8 w-2/3 bg-muted/60 animate-pulse rounded-md" data-index={1} />
        <div className="h-8 w-1/2 bg-muted/60 animate-pulse rounded-md" data-index={2} />
      </div>;
  }
  return <nav className="flex flex-col gap-1 px-2">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} onClick={onNavigate} data-index={i} className="text-foreground text-lg py-3 px-2 border-b border-border last:border-b-0 tracking-[0.02em]" style={{
      fontFamily: "var(--font-default)"
    }}>
          {item.label}
        </Link>)}
    </nav>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="1790" id="header" className={`sticky top-0 z-50 w-full transition-all duration-[280ms] ease-out bg-background text-foreground ${scrolled ? "bg-background/75 backdrop-blur-lg border-b border-border" : "bg-background border-b border-transparent"}`}>
      <div className="pointer-events-none absolute inset-0 opacity-[0.05] mix-blend-multiply" style={{
      backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.15 0 0 0 0 0.12 0 0 0 0 0.08 0 0 0 0.9 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")"
    }} aria-hidden="true" />

      <div className="relative mx-auto max-w-[1320px] px-5 md:px-8 lg:px-14">
        <div className="grid grid-cols-12 items-center h-20 md:h-24 gap-4">
          <div className="col-span-6 md:col-span-3 flex items-center">
            <Link to="/" aria-label="Dott. Mirko Colloca — Home" className="inline-flex items-center">
              <WvcLogo className="!opacity-[1] !bg-[rgba(255,255,255,1)] !text-[rgba(91,114,147,1)] !w-[90px] rounded-lg !h-[80px] [&_svg]:!opacity-[1] [&_svg]:!bg-[rgba(255,255,255,1)] [&_svg]:!text-[rgba(91,114,147,1)] [&_svg]:!w-[90px] [&_svg]:rounded-lg [&_svg]:!h-[80px]" />
            </Link>
          </div>

          <div className="hidden md:flex col-span-6 items-center justify-center">
            <MenuProvider menu_id="2" className="header-menu">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          <div className="col-span-6 md:col-span-3 flex items-center justify-end gap-3">
            <Link to="/ContactsPage" className="hidden md:inline-flex">
              <Button className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 px-7 py-5 h-auto text-sm font-medium tracking-[0.02em] shadow-md" style={{
              fontFamily: "var(--font-default)"
            }}>
                Prenota Visita
              </Button>
            </Link>

            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Apri menu" className="md:hidden h-11 w-11 text-foreground hover:bg-muted/50 rounded-lg">
                  <Menu className="h-6 w-6" strokeWidth={1.5} />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-background border-l border-border w-[88%] sm:max-w-sm p-0 flex flex-col">
                <SheetHeader className="px-6 pt-6 pb-4 border-b border-border">
                  <SheetTitle asChild>
                    <Link to="/" onClick={() => setMobileOpen(false)} className="inline-flex items-center">
                      <WvcLogo className="h-8 w-auto" />
                    </Link>
                  </SheetTitle>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto py-6">
                  <div className="px-4 mb-4">
                    <span className="text-[0.6875rem] uppercase tracking-[0.18em] text-muted-foreground" style={{
                    fontFamily: "var(--font-mono)"
                  }}>
                      Navigazione
                    </span>
                  </div>
                  <MenuProvider menu_id="2" className="header-menu-mobile">
                    <MobileMenuConsumer onNavigate={() => setMobileOpen(false)} />
                  </MenuProvider>
                </div>

                <div className="px-6 pb-8 pt-4 border-t border-border">
                  <Link to="/ContactsPage" onClick={() => setMobileOpen(false)}>
                    <Button className="w-full rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 py-6 h-auto text-base font-medium tracking-[0.02em]" style={{
                    fontFamily: "var(--font-default)"
                  }}>
                      Prenota Visita
                    </Button>
                  </Link>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {scrolled && <div className="relative">
          <div className="mx-auto max-w-[1320px] px-5 md:px-8 lg:px-14">
            <div className="flex items-center">
              <div className="flex-1 h-px bg-border" />
              <div className="h-1.5 w-1.5 rounded-lg bg-primary" aria-hidden="true" />
            </div>
          </div>
        </div>}
    </section>;
}